<script setup lang="ts">
import { computed } from 'vue'

import type { ReportColumn } from '../../models/column.types'
import type { ReportFilter } from '../../models/filter.types'
import { isPercentageReportColumn } from '../../service/reportColumnKind'
import { getReportFilterModeDefinition } from '../../service/reportFilterModes'
import MetricLabel from '../MetricLabel.vue'

import { useI18n } from '@/shared/i18n'
import { useFormat } from '@/shared/lib/format'

const props = defineProps<{
  filter: ReportFilter
  column?: ReportColumn | undefined
}>()

const { t } = useI18n()
const format = useFormat()
const modeLabel = computed(() => t(getReportFilterModeDefinition(props.filter.match_mode).labelKey))
const selectionSummary = computed(
  () =>
    Array.isArray(props.filter.value) &&
    props.filter.value.length > 2 &&
    ['in', 'not_in'].includes(props.filter.match_mode),
)
const percentage = computed(
  () =>
    props.filter.is_change === true ||
    isPercentageReportColumn(props.column) ||
    props.filter.sub_type === 'percentage',
)
const displayValue = computed(() => formatValue(props.filter.value))
const directionLabel = computed(() =>
  props.filter.direction ? t(`reports.filters.${props.filter.direction}`) : '',
)

function formatValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return ''
  if (Array.isArray(value)) {
    if (selectionSummary.value) {
      return t('reports.filters.selectedCount', { count: value.length })
    }
    return value.map(formatScalarWithUnit).join(' – ')
  }
  return formatScalarWithUnit(value)
}

function formatScalarWithUnit(value: unknown): string {
  const scalar = formatScalar(value)
  return scalar && percentage.value ? `${scalar}${t('reports.filters.percentSign')}` : scalar
}

function formatScalar(value: unknown): string {
  if (typeof value === 'number') return format.formatNumber(value)
  return String(value)
}
</script>

<template>
  <span
    data-pdf-filter-view
    class="filter-chip inline-flex min-w-0 items-center gap-2xs whitespace-nowrap"
  >
    <span v-if="filter.is_change" class="filter-chip__change opacity-80">
      {{ t('reports.filters.changePercentage') }}
    </span>
    <MetricLabel :field="filter.field" :column="column" />
    <span class="filter-chip__mode opacity-80">{{ modeLabel }}</span>
    <span v-if="displayValue" class="filter-chip__value font-semibold">{{ displayValue }}</span>
    <span v-if="directionLabel" class="filter-chip__direction opacity-80">{{
      directionLabel
    }}</span>
  </span>
</template>
